import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiGetWordleWord, apiValidateWord } from '../utils/api';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const MAX_ATTEMPTS  = 6;
const WORD_LENGTH   = 5;
const GAME_URL      = 'https://wordzingo.com/wordle';
const KEYBOARD_ROWS = [
  ['Q','W','E','R','T','Y','U','I','O','P'],
  ['A','S','D','F','G','H','J','K','L'],
  ['ENTER','Z','X','C','V','B','N','M','⌫'],
];
const STATE_EMOJI = { correct: '🟩', present: '🟨', absent: '⬛' };

function getLetterStates(guess, target) {
  const result = Array(WORD_LENGTH).fill('absent');
  const tArr   = target.split('');
  const used   = Array(WORD_LENGTH).fill(false);
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guess[i] === tArr[i]) { result[i] = 'correct'; used[i] = true; }
  }
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (result[i] === 'correct') continue;
    for (let j = 0; j < WORD_LENGTH; j++) {
      if (!used[j] && guess[i] === tArr[j]) { result[i] = 'present'; used[j] = true; break; }
    }
  }
  return result;
}

function buildShareText(guesses, status) {
  const score = status === 'won' ? guesses.length : 'X';
  const grid  = guesses.map(g => g.states.map(s => STATE_EMOJI[s]).join('')).join('\n');
  return `🟩 WordZingo Wordle ${score}/${MAX_ATTEMPTS}\n\n${grid}\n\n🎯 Think you can beat me?\n${GAME_URL}`;
}

/* ─── Share Modal ────────────────────────────────────────────────────────── */
function ShareModal({ guesses, status, onClose, onNewGame }) {
  const [copied, setCopied] = useState(false);
  const shareText = buildShareText(guesses, status);
  const score     = status === 'won' ? guesses.length : 'X';

  function copy() {
    navigator.clipboard.writeText(shareText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  const encText = encodeURIComponent(shareText);
  const encUrl  = encodeURIComponent(GAME_URL);

  const SHARE_BTNS = [
    { label: '𝕏', bg: '#000', href: `https://twitter.com/intent/tweet?text=${encText}` },
    { label: '💬', bg: '#25d366', href: `https://wa.me/?text=${encText}` },
    { label: 'f', bg: '#1877f2', href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}` },
    { label: 'in', bg: '#0a66c2', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}` },
  ];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 18, padding: '1.2rem 1.3rem 1rem',
          maxWidth: 300, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          textAlign: 'center', position: 'relative',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close ✕ */}
        <button onClick={onClose} style={{
          position: 'absolute', top: 10, right: 12, background: 'none',
          border: 'none', fontSize: '1.2rem', color: '#9ca3af', cursor: 'pointer',
          lineHeight: 1, padding: 0,
        }}>✕</button>

        {/* Header */}
        <div style={{ fontSize: '1.6rem', marginBottom: 2 }}>{status === 'won' ? '🎉' : '😔'}</div>
        <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e1b4b', marginBottom: 2 }}>
          {status === 'won' ? `Solved in ${score}/6!` : 'Better luck next time!'}
        </div>
        <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '0.8rem' }}>
          Challenge your friends — can they beat you?
        </div>

        {/* Emoji grid */}
        <div style={{
          fontFamily: 'monospace', fontSize: '1.2rem', lineHeight: 1.6,
          background: '#f8fafc', borderRadius: 10, padding: '0.6rem 0.8rem',
          marginBottom: '0.8rem', letterSpacing: 2,
        }}>
          {guesses.map((g, i) => (
            <div key={i}>{g.states.map(s => STATE_EMOJI[s]).join('')}</div>
          ))}
        </div>

        {/* Share text preview */}
        <div style={{
          background: '#f1f5f9', borderRadius: 10, padding: '0.5rem 0.7rem',
          fontSize: '0.72rem', color: '#475569', textAlign: 'left',
          marginBottom: '0.6rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap',
          wordBreak: 'break-word', lineHeight: 1.6,
        }}>
          {shareText}
        </div>

        {/* Copy button */}
        <button onClick={copy} style={{
          width: '100%', borderRadius: 50, border: 'none', padding: '0.55rem',
          fontWeight: 700, fontFamily: 'Nunito, sans-serif', fontSize: '0.88rem',
          marginBottom: '0.5rem', cursor: 'pointer',
          background: copied ? '#10b981' : '#4f46e5', color: '#fff', transition: 'background 0.2s',
        }}>
          {copied ? '✓ Copied!' : '📋 Copy Result'}
        </button>

        {/* Social icons row */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: '0.8rem' }}>
          {SHARE_BTNS.map(({ label, bg, href }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer"
              style={{
                width: 40, height: 40, borderRadius: '50%', background: bg,
                color: '#fff', textDecoration: 'none', fontWeight: 800, fontSize: '0.85rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
              {label}
            </a>
          ))}
        </div>

        {/* New game */}
        <button onClick={() => { onNewGame(); onClose(); }} style={{
          borderRadius: 50, border: '1.5px solid #e2e8f0', background: 'none',
          padding: '0.38rem 1rem', fontWeight: 700, fontFamily: 'Nunito, sans-serif',
          fontSize: '0.82rem', cursor: 'pointer', color: '#374151',
        }}>
          🔄 New Game
        </button>
      </div>
    </div>
  );
}

/* ─── Main Game ──────────────────────────────────────────────────────────── */
export default function WordleGame() {
  const [target, setTarget]       = useState('');
  const [guesses, setGuesses]     = useState([]);
  const [current, setCurrent]     = useState('');
  const [status, setStatus]       = useState('playing');
  const [keyStates, setKeyStates] = useState({});
  const [shake, setShake]         = useState(false);
  const [message, setMessage]     = useState('');
  const [loading, setLoading]     = useState(true);
  const [showShare, setShowShare] = useState(false);

  async function fetchWord() {
    setLoading(true);
    try {
      const { data } = await apiGetWordleWord();
      setTarget(data.word.toUpperCase());
    } catch { setTarget('CRANE'); }
    finally { setLoading(false); }
  }

  useEffect(() => { fetchWord(); }, []);

  function newGame() {
    setGuesses([]); setCurrent(''); setStatus('playing');
    setKeyStates({}); setMessage(''); setShowShare(false);
    fetchWord();
  }

  function showMsg(msg, duration = 2000) {
    setMessage(msg);
    setTimeout(() => setMessage(''), duration);
  }

  const handleKey = useCallback(async (key) => {
    if (status !== 'playing') return;
    if (key === 'ENTER') {
      if (current.length < WORD_LENGTH) {
        setShake(true); setTimeout(() => setShake(false), 400);
        showMsg('Not enough letters!'); return;
      }
      try {
        const { data } = await apiValidateWord(current.toLowerCase());
        if (!data.valid) {
          setShake(true); setTimeout(() => setShake(false), 400);
          showMsg('Not in word list!'); return;
        }
      } catch { /* allow if API down */ }

      const states     = getLetterStates(current, target);
      const newGuess   = { word: current, states };
      const newGuesses = [...guesses, newGuess];
      setGuesses(newGuesses);

      const ks = { ...keyStates };
      current.split('').forEach((ch, i) => {
        const prev = ks[ch]; const s = states[i];
        if (prev === 'correct') return;
        if (prev === 'present' && s !== 'correct') return;
        ks[ch] = s;
      });
      setKeyStates(ks);

      if (current === target) {
        setStatus('won');
        showMsg(['Amazing! 🎉','Brilliant! ⭐','Superb! 🌟','Great job! 🎊'][Math.floor(Math.random()*4)], 2500);
        setTimeout(() => setShowShare(true), 2000);
      } else if (newGuesses.length >= MAX_ATTEMPTS) {
        setStatus('lost');
        showMsg('Better luck next time! 😔', 3500);
        setTimeout(() => setShowShare(true), 3000);
      }
      setCurrent('');
    } else if (key === '⌫' || key === 'BACKSPACE') {
      setCurrent(c => c.slice(0, -1));
    } else if (/^[A-Z]$/.test(key) && current.length < WORD_LENGTH) {
      setCurrent(c => c + key);
    }
  }, [current, guesses, keyStates, status, target]);

  useEffect(() => {
    function onKey(e) { handleKey(e.key === 'Backspace' ? '⌫' : e.key.toUpperCase()); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleKey]);

  if (loading) return (
    <div className="game-page text-center">
      <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
      <p className="mt-2 fw-bold">Loading Wordle…</p>
    </div>
  );

  return (
    <div className="game-page">
      <PageSEO page="wordle" />

      {showShare && (
        <ShareModal
          guesses={guesses}
          status={status}
          onClose={() => setShowShare(false)}
          onNewGame={newGame}
        />
      )}

      <div className="container" style={{ maxWidth: 480 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🟩</span>
          <h1>Wordle</h1>
          <p>Guess the 5-letter word in 6 tries!</p>
        </div>

        {message && (
          <div className="text-center fw-bold py-2 px-3 rounded-3 mb-3"
            style={{ background: '#1e1b4b', color: '#fff', fontSize: '1.05rem' }}>
            {message}
          </div>
        )}

        {/* Grid */}
        <div className="wordle-grid mb-4">
          {Array.from({ length: MAX_ATTEMPTS }, (_, rowIdx) => {
            const guess    = guesses[rowIdx];
            const isActive = rowIdx === guesses.length && status === 'playing';
            const letters  = guess ? guess.word.split('') : (isActive ? current.split('') : []);
            return (
              <div key={rowIdx} className={`wordle-row ${isActive && shake ? 'shake' : ''}`}>
                {Array.from({ length: WORD_LENGTH }, (_, colIdx) => {
                  const letter = letters[colIdx] || '';
                  let cls = '';
                  if (guess) cls = guess.states[colIdx];
                  else if (letter) cls = 'filled';
                  if (isActive && colIdx === current.length - 1 && letter) cls += ' active';
                  return (
                    <div key={colIdx} className={`wordle-cell ${cls}`}>{letter}</div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {status === 'lost' && (
          <div className="alert alert-custom mb-3 text-center" style={{ background: '#fee2e2', color: '#991b1b' }}>
            💀 The word was <strong style={{ letterSpacing: 2 }}>{target}</strong>. Better luck next time!
          </div>
        )}

        {/* Keyboard */}
        <div className="wordle-keyboard">
          {KEYBOARD_ROWS.map((row, i) => (
            <div key={i} className="kb-row">
              {row.map(key => (
                <button key={key}
                  className={`kb-key ${key.length > 1 ? 'wide' : ''} ${keyStates[key] || ''}`}
                  onClick={() => handleKey(key)}
                >{key}</button>
              ))}
            </div>
          ))}
        </div>

        {/* Game over actions */}
        {status !== 'playing' && (
          <div className="text-center mt-4 d-flex flex-wrap justify-content-center gap-2">
            <button className="solve-btn" onClick={newGame}>🔄 New Game</button>
            <button className="btn fw-bold"
              style={{ borderRadius: 50, background: '#4f46e5', color: '#fff', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}
              onClick={() => setShowShare(true)}>
              📤 Share Result
            </button>
            <Link to="/wordle-solver" className="btn btn-sm fw-bold"
              style={{ borderRadius: 50, background: 'var(--border)', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}>
              💡 Wordle Solver
            </Link>
          </div>
        )}

        <SeoContent game="wordle" />
      </div>
    </div>
  );
}
